// Admin "Comptes" tab — unified management of admins + Espace Membre accounts.
// Replaces the old "Admins" tab. Backed by /api/admin/accounts endpoints.
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const ROLE_LABELS = {
  fr: {
    admin: "Administrateur",
    organisateur: "Organisateur",
    benevole: "Bénévole",
    artiste: "Artiste",
    participant: "Participant",
  },
  en: {
    admin: "Administrator",
    organisateur: "Organiser",
    benevole: "Volunteer",
    artiste: "Artist",
    participant: "Participant",
  },
};

const ROLE_COLORS = {
  admin: "var(--cinnabar)",
  organisateur: "var(--lapis)",
  benevole: "var(--moss)",
  artiste: "var(--gold)",
  participant: "var(--ink-2)",
};

const ALL_ROLES = ["admin", "organisateur", "benevole", "artiste", "participant"];

export default function AccountsTab({ locale }) {
  const isFr = locale === "fr";
  const labels = ROLE_LABELS[locale] || ROLE_LABELS.fr;

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, admins: 0, members: 0, by_role: {} });
  const [currentEmail, setCurrentEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [inviting, setInviting] = useState(false);
  const [flash, setFlash] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/accounts");
      setItems(data.items || []);
      setCounts(data.counts || { total: 0, admins: 0, members: 0, by_role: {} });
      setCurrentEmail(data.current_email || "");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((it) => {
    if (filter !== "all" && it.role !== filter) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = `${it.name} ${it.email} ${it.role}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 6000); };

  const onResetPassword = async (it) => {
    if (!window.confirm(isFr
      ? `Réinitialiser le mot de passe de ${it.email} ?\nUn email lui sera envoyé.`
      : `Reset password for ${it.email}?\nAn email will be sent.`)) return;
    try {
      const { data } = await api.post(`/admin/accounts/${it.kind}/${it.id}/reset-password`);
      showFlash(isFr
        ? `Mot de passe réinitialisé. Temporaire (à communiquer si l'email n'arrive pas) : ${data.temp_password}`
        : `Password reset. Temporary (share if email fails): ${data.temp_password}`);
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Erreur");
    }
  };

  const onChangeRole = async (it, newRole) => {
    if (newRole === it.role) return;
    if (!window.confirm(isFr
      ? `Changer le rôle de ${it.name} en « ${labels[newRole]} » ?`
      : `Change ${it.name}'s role to "${labels[newRole]}"?`)) return;
    try {
      await api.patch(`/admin/accounts/${it.kind}/${it.id}/role`, { role: newRole });
      showFlash(isFr ? "Rôle mis à jour." : "Role updated.");
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Erreur");
    }
  };

  const onToggleActive = async (it) => {
    try {
      await api.patch(`/admin/accounts/${it.kind}/${it.id}/active`);
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (it) => {
    if (!window.confirm(isFr
      ? `Résilier définitivement le compte de ${it.email} ?\nCette action est irréversible.`
      : `Permanently terminate ${it.email}'s account?\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admin/accounts/${it.kind}/${it.id}`);
      showFlash(isFr ? "Compte supprimé." : "Account deleted.");
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="accounts-tab" className="space-y-6">
      <SignaturePanel locale={locale} />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl">{isFr ? "Comptes" : "Accounts"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Gestion unifiée des administrateurs et des comptes de l'Espace Membre — rôles, mots de passe, désactivation, suppression."
              : "Unified management of administrators and Espace Membre accounts — roles, passwords, suspension, deletion."}
          </p>
          <div className="flex gap-3 mt-2 font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
            <span>TOTAL : {counts.total}</span>
            <span>· {isFr ? "ADMINS" : "ADMINS"} : {counts.admins}</span>
            <span>· {isFr ? "MEMBRES" : "MEMBERS"} : {counts.members}</span>
          </div>
        </div>
        <button data-testid="account-invite-btn" onClick={() => setInviting(true)} className="btn btn-primary">
          + {isFr ? "Ouvrir un compte (email d'invitation)" : "Open account (invite email)"}
        </button>
      </div>

      {flash && (
        <div data-testid="account-flash"
             className="p-3 border" style={{ background: "var(--parchment-2)", borderColor: "var(--gold)", color: "var(--ink)" }}>
          <span className="font-body text-sm">{flash}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        {[
          { v: "all", lbl: isFr ? "Tous" : "All", count: counts.total },
          ...ALL_ROLES.map((r) => ({ v: r, lbl: labels[r], count: counts.by_role?.[r] || 0 })),
        ].map((f) => (
          <button key={f.v}
                  data-testid={`accounts-filter-${f.v}`}
                  onClick={() => setFilter(f.v)}
                  className="font-caps text-[0.62rem] px-3 py-1.5 border"
                  style={{
                    background: filter === f.v ? "var(--ink)" : "transparent",
                    color: filter === f.v ? "white" : "var(--ink)",
                    borderColor: "var(--ink)",
                    letterSpacing: "0.18em",
                  }}>
            {f.lbl} · {f.count}
          </button>
        ))}
        <input
          data-testid="accounts-search"
          type="search" placeholder={isFr ? "Rechercher…" : "Search…"}
          value={query} onChange={(e) => setQuery(e.target.value)}
          className="input-classic ml-2" style={{ flex: 1, minWidth: 180 }}
        />
      </div>

      {loading && <p className="text-center" style={{ color: "var(--muted)" }}>{isFr ? "Chargement…" : "Loading…"}</p>}

      {!loading && filtered.length === 0 && (
        <p className="text-center bg-white border p-8" style={{ borderColor: "var(--border)", color: "var(--muted)" }}>
          {isFr ? "Aucun compte trouvé." : "No accounts found."}
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-white border overflow-x-auto" style={{ borderColor: "var(--border)" }}>
          <table className="w-full font-body text-sm">
            <thead>
              <tr style={{ background: "var(--parchment-2)" }}>
                <th className="text-left p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "NOM" : "NAME"}</th>
                <th className="text-left p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "EMAIL" : "EMAIL"}</th>
                <th className="text-left p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "RÔLE" : "ROLE"}</th>
                <th className="text-center p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "STATUT" : "STATUS"}</th>
                <th className="text-right p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const isSelf = (it.email || "").toLowerCase() === (currentEmail || "").toLowerCase();
                return (
                  <tr key={`${it.kind}-${it.id}`} data-testid={`account-row-${it.id}`}
                      className="border-t" style={{ borderColor: "var(--border)", opacity: it.active === false ? 0.55 : 1 }}>
                    <td className="p-3 font-display" style={{ color: "var(--ink)" }}>
                      {it.name || "—"}
                      {isSelf && (
                        <span className="ml-2 font-caps text-[0.55rem]" style={{ color: "var(--gold)", letterSpacing: "0.18em" }}>
                          ({isFr ? "VOUS" : "YOU"})
                        </span>
                      )}
                      {it.is_root && (
                        <span className="ml-2 font-caps text-[0.55rem]" style={{ color: "var(--cinnabar)", letterSpacing: "0.18em" }}>
                          (ROOT)
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs">{it.email}</td>
                    <td className="p-3">
                      <select
                        data-testid={`account-role-${it.id}`}
                        value={it.role}
                        disabled={isSelf || it.is_root}
                        onChange={(e) => onChangeRole(it, e.target.value)}
                        className="font-caps text-[0.62rem] px-2 py-1 border"
                        style={{
                          borderColor: ROLE_COLORS[it.role] || "var(--border)",
                          color: ROLE_COLORS[it.role] || "var(--ink)",
                          letterSpacing: "0.16em",
                          background: "transparent",
                        }}>
                        {ALL_ROLES.map((r) => (
                          <option key={r} value={r}>{labels[r]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3 text-center">
                      <button data-testid={`account-active-${it.id}`}
                              onClick={() => onToggleActive(it)}
                              disabled={isSelf || it.is_root}
                              className="font-caps text-[0.6rem] px-2.5 py-1 border"
                              style={{
                                borderColor: it.active !== false ? "var(--moss)" : "var(--muted)",
                                color: it.active !== false ? "var(--moss)" : "var(--muted)",
                                letterSpacing: "0.15em",
                              }}>
                        {it.active !== false ? (isFr ? "ACTIF" : "ACTIVE") : (isFr ? "DÉSACTIVÉ" : "DISABLED")}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`account-resetpwd-${it.id}`}
                                onClick={() => onResetPassword(it)}
                                className="font-caps text-[0.6rem] px-2.5 py-1 border"
                                style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.15em" }}>
                          {isFr ? "RESET MDP" : "RESET PWD"}
                        </button>
                        <button data-testid={`account-delete-${it.id}`}
                                onClick={() => onDelete(it)}
                                disabled={isSelf || it.is_root}
                                className="font-caps text-[0.6rem] px-2.5 py-1 border"
                                style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
                          {isFr ? "RÉSILIER" : "TERMINATE"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {inviting && (
        <InviteAccountModal
          locale={locale}
          labels={labels}
          onClose={() => setInviting(false)}
          onSent={(msg) => { setInviting(false); showFlash(msg); load(); }}
        />
      )}
    </div>
  );
}

function InviteAccountModal({ locale, labels, onClose, onSent }) {
  const isFr = locale === "fr";
  const [form, setForm] = useState({
    email: "", name: "", role: "benevole", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/accounts/invite", form);
      onSent(isFr
        ? `Compte ouvert pour ${form.email}. Mot de passe temporaire envoyé par email : ${data.temp_password}`
        : `Account opened for ${form.email}. Temporary password sent by email: ${data.temp_password}`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="account-invite-modal" onClick={onClose}
         className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
         style={{ background: "rgba(20,16,12,0.6)" }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white border max-w-lg w-full p-6 space-y-4 my-8"
            style={{ borderColor: "var(--border)", boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}>
        <div>
          <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
            {isFr ? "OUVERTURE D'UN COMPTE" : "OPEN AN ACCOUNT"}
          </div>
          <p className="font-body text-sm mt-2" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Un mot de passe temporaire sera généré et envoyé par e-mail (Brevo) à la personne invitée."
              : "A temporary password will be generated and emailed (Brevo) to the invited person."}
          </p>
        </div>

        <label className="block">
          <span className="label-caps">{isFr ? "EMAIL *" : "EMAIL *"}</span>
          <input data-testid="account-invite-email" required type="email"
                 className="input-classic mt-1 w-full"
                 value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} />
        </label>

        <label className="block">
          <span className="label-caps">{isFr ? "NOM COMPLET *" : "FULL NAME *"}</span>
          <input data-testid="account-invite-name" required
                 className="input-classic mt-1 w-full"
                 value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
        </label>

        <label className="block">
          <span className="label-caps">{isFr ? "RÔLE *" : "ROLE *"}</span>
          <select data-testid="account-invite-role" required
                  className="input-classic mt-1 w-full"
                  value={form.role} onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}>
            {ALL_ROLES.map((r) => (
              <option key={r} value={r}>{labels[r]}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="label-caps">{isFr ? "MESSAGE PERSONNALISÉ (optionnel)" : "PERSONAL MESSAGE (optional)"}</span>
          <textarea data-testid="account-invite-message" rows={3}
                    className="input-classic mt-1 w-full"
                    placeholder={isFr ? "Quelques mots ajoutés dans l'email d'ouverture du compte…" : "A few words added to the welcome email…"}
                    value={form.message} onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))} />
        </label>

        {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-outline" style={{ flex: 1 }}>
            {isFr ? "Annuler" : "Cancel"}
          </button>
          <button type="submit" data-testid="account-invite-submit" disabled={busy}
                  className="btn btn-primary" style={{ flex: 1 }}>
            {busy ? "…" : (isFr ? "Envoyer l'invitation" : "Send invitation")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// Personal signature panel — appended to outbound emails sent BY this admin.
// Optional. Stored on the admin user document so each admin has their own.
// ============================================================================
function SignaturePanel({ locale }) {
  const isFr = locale === "fr";
  const [sig, setSig] = useState({ enabled: false, name: "", function: "", phone: "" });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/admin/me/signature")
      .then((r) => mounted && setSig({
        enabled: !!r.data.enabled,
        name: r.data.name || "",
        function: r.data.function || "",
        phone: r.data.phone || "",
      }))
      .finally(() => mounted && setLoaded(true));
    return () => { mounted = false; };
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setSaved(false);
    try {
      await api.patch("/admin/me/signature", sig);
      setSaved(true);
      setTimeout(() => setSaved(false), 3500);
    } finally { setBusy(false); }
  };

  if (!loaded) return null;

  return (
    <form onSubmit={save} data-testid="signature-panel"
          className="bg-white border p-5"
          style={{ borderColor: "var(--border)", borderLeft: "3px solid var(--gold)" }}>
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="font-caps text-[0.62rem]" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
            {isFr ? "MA SIGNATURE EMAIL · OPTIONNELLE" : "MY EMAIL SIGNATURE · OPTIONAL"}
          </div>
          <p className="font-body text-sm mt-1" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Si activée, votre nom, fonction et téléphone seront ajoutés en bas des emails que vous envoyez depuis APLEO."
              : "When enabled, your name, function and phone are appended at the bottom of emails you send from APLEO."}
          </p>
        </div>
        <label className="flex items-center gap-2 font-body text-sm shrink-0">
          <input type="checkbox"
                 data-testid="signature-enabled"
                 checked={sig.enabled}
                 onChange={(e) => setSig((s) => ({ ...s, enabled: e.target.checked }))} />
          {isFr ? "Activée" : "Enabled"}
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"
           style={{ opacity: sig.enabled ? 1 : 0.55 }}>
        <label className="block">
          <span className="label-caps text-[0.58rem]">{isFr ? "NOM" : "NAME"}</span>
          <input data-testid="signature-name"
                 disabled={!sig.enabled}
                 className="input-classic mt-1 w-full"
                 value={sig.name}
                 onChange={(e) => setSig((s) => ({ ...s, name: e.target.value }))} />
        </label>
        <label className="block">
          <span className="label-caps text-[0.58rem]">{isFr ? "FONCTION" : "FUNCTION"}</span>
          <input data-testid="signature-function"
                 disabled={!sig.enabled}
                 placeholder={isFr ? "ex. Trésorier, Présidente…" : "e.g. Treasurer, President…"}
                 className="input-classic mt-1 w-full"
                 value={sig.function}
                 onChange={(e) => setSig((s) => ({ ...s, function: e.target.value }))} />
        </label>
        <label className="block">
          <span className="label-caps text-[0.58rem]">{isFr ? "TÉLÉPHONE" : "PHONE"}</span>
          <input data-testid="signature-phone"
                 disabled={!sig.enabled}
                 placeholder="+33 …"
                 className="input-classic mt-1 w-full"
                 value={sig.phone}
                 onChange={(e) => setSig((s) => ({ ...s, phone: e.target.value }))} />
        </label>
      </div>

      <div className="flex items-center justify-end gap-3 mt-4">
        {saved && (
          <span data-testid="signature-saved" className="font-caps text-[0.6rem]"
                style={{ color: "var(--moss)", letterSpacing: "0.18em" }}>
            ✓ {isFr ? "ENREGISTRÉ" : "SAVED"}
          </span>
        )}
        <button type="submit"
                data-testid="signature-save"
                disabled={busy}
                className="font-caps text-[0.62rem] px-4 py-2 border"
                style={{ borderColor: "var(--ink)", color: "var(--ink)", letterSpacing: "0.18em", background: "transparent" }}>
          {busy ? "…" : (isFr ? "ENREGISTRER" : "SAVE")}
        </button>
      </div>
    </form>
  );
}

